import { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { queryClient, authorizedFetch } from '@/lib/queryClient';
import { useQuery } from '@tanstack/react-query';
import { useProjectsStore } from '@/store/projects';
import { FolderOpen, CloudUpload } from 'lucide-react';

interface FileUploadProps {
  projectId: string;
  onClose: () => void;
  onFileUploaded?: (fileData: any) => void;
}

export default function FileUpload({ projectId, onClose, onFileUploaded }: FileUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const { toast } = useToast();
  const loadProjects = useProjectsStore((state) => state.loadProjects);

  // Load runtime upload limits from server config
  const { data: cfg } = useQuery({
    queryKey: ['/api/config'],
    queryFn: async () => {
      const res = await authorizedFetch('/api/config');
      return res.json();
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const uploadCaps = cfg?.uploads || {};
  const maxBytes: number = Number(uploadCaps.maxFileSize ?? 100 * 1024 * 1024);
  const maxRows: number | undefined = typeof uploadCaps.maxRows === 'number' ? uploadCaps.maxRows : 100000;
  const maxCols: number | undefined = typeof uploadCaps.maxColumns === 'number' ? uploadCaps.maxColumns : 50;
  // Temporarily restrict to CSV-only for user-facing upload
  const allowedExts: string[] = ['.csv'];

  const maxMbLabel = useMemo(() => `${Math.floor(maxBytes / (1024 * 1024))}MB`, [maxBytes]);
  const limitsLabel = useMemo(() => {
    const parts: string[] = [];
    const exts = allowedExts.map((s) => s.replace(/^\./, '').toUpperCase());
    parts.push(`Supports ${exts.join(', ')}`);
    parts.push(`Max ${maxMbLabel}`);
    if (maxRows && maxCols) parts.push(`${maxRows.toLocaleString()} rows × ${maxCols.toLocaleString()} columns`);
    return parts.join(' • ');
  }, [allowedExts, maxMbLabel, maxRows, maxCols]);

  const handleFiles = async (files: FileList) => {
    if (files.length === 0) return;
    
    const file = files[0];
    
    // Validate file type
    const allowedTypes = allowedExts;
    const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (!allowedTypes.includes(fileExt)) {
      toast({
        title: "Invalid file type",
        description: `Allowed: ${allowedTypes.map((s) => s.replace(/^\./,'').toUpperCase()).join(', ')}`,
        variant: "destructive",
      });
      return;
    }
    
    // Validate file size (100MB limit)
    if (file.size > maxBytes) {
      toast({
        title: "File too large",
        description: `Please upload a file smaller than ${maxMbLabel}`,
        variant: "destructive",
      });
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);

      const formData = new FormData();
      formData.append('file', file);

      // Simulate progress more realistically
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev < 30) return prev + 15;
          if (prev < 60) return prev + 10;
          if (prev < 85) return prev + 5;
          return Math.min(prev + 2, 95);
        });
      }, 300);

      const response = await authorizedFetch(`/api/projects/${projectId}/upload`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      
      if (!response.ok) {
        setUploadProgress(0);
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }
      
      setUploadProgress(100);

      const uploadedFile = await response.json();
      
      // Invalidate files query to refresh the list
      // Align with chat's query key: scopedKey(['files', userId, pid])
      try {
        // Broad invalidation by partial key 'files' to catch all per-user/project scopes
        queryClient.invalidateQueries({ queryKey: ['files'] });
      } catch {}

      // Refresh project aggregates such as datasetsCount for dashboard cards
      try {
        await loadProjects();
      } catch (err) {
        console.error('Failed to refresh projects after upload', err);
      }
      
      toast({
        title: "File uploaded successfully",
        description: `${file.name} has been uploaded to your project`,
      });
      try {
        const norm = (uploadedFile?.metadata && (uploadedFile.metadata as any).normalization) || null;
        if (norm && norm.header_changed) {
          const pairs = Object.entries((norm.header_map || {}) as Record<string, string>)
            .filter(([canon, orig]) => String(canon) !== String(orig));
          const example = pairs.slice(0, 3).map(([canon, orig]) => `‘${orig}’ → ‘${canon}’`).join(', ');
          toast({
            title: "Cleaned headers",
            description: example || "Removed hidden characters and normalized spacing",
          });
        }
      } catch {}
      
      // Optionally fetch table rows for preview (backend returns CSV)
      let table: any[] | null = null;
      try {
        const resp = await authorizedFetch(`/api/files/${uploadedFile.id}/data`);
        if (resp.ok) {
          const t = await resp.json();
          if (Array.isArray(t) && t.length > 0) {
            table = t;
            (window as any).lastDataTable = t;
            (window as any).lastDataTotalRows = t.length;
          }
        }
      } catch {}

      // Pass the uploaded file data back
      if (onFileUploaded) {
        onFileUploaded(uploadedFile);
      }
      // Emit a browser event for Data tab; chat will fetch canonical fingerprint separately
      try {
        const detail = {
          fileId: uploadedFile.id,
          fingerprintId: uploadedFile.fingerprintId,
          fingerprint: uploadedFile.fingerprint,
          rows: uploadedFile?.fingerprint?.rows,
          columns: uploadedFile?.fingerprint?.columns,
          patterns: uploadedFile?.fingerprint?.patterns,
          suggestions: uploadedFile?.fingerprint?.suggestions,
          filename: uploadedFile?.filename,
          table: table || null,
        };
        window.dispatchEvent(new CustomEvent('lumi:dataAnalyzed' as any, { detail } as any));
      } catch {}
      
      onClose();
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "There was an error uploading your file. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="file-upload-modal">
        <DialogHeader>
          <DialogTitle>Upload Data File</DialogTitle>
        </DialogHeader>

        {isUploading ? (
          <div className="py-8 text-center" data-testid="upload-progress">
            <CloudUpload className="h-16 w-16 text-primary mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Uploading...</h3>
            <Progress value={uploadProgress} className="w-full mb-2" />
            <p className="text-sm text-muted-foreground">{uploadProgress}% complete</p>
          </div>
        ) : (
          <>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              data-testid="drop-zone"
            >
              <CloudUpload className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">
                {dragActive ? 'Drop your file here' : 'Upload your data'}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Drag and drop or click to select a file
              </p>
              <p className="text-xs text-muted-foreground">
                {limitsLabel}
              </p>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="flex-1" data-testid="button-cancel">
                Cancel
              </Button>
              <Button
                onClick={() => document.getElementById('file-input')?.click()}
                className="flex-1"
                data-testid="button-browse"
              >
                <FolderOpen className="mr-2 h-4 w-4" />
                Browse Files
              </Button>
            </div>

            <input
              id="file-input"
              type="file"
              className="hidden"
              accept={allowedExts.join(',')}
              onChange={handleFileInput}
              data-testid="file-input"
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
